import React from 'react';
import { Link } from 'react-router-dom';

import { 
    Code, 
    Smartphone, 
    LineChart, 
    PenTool, 
    CircleDashed,
    Keyboard,
    QrCode,
    ArrowRight,
    Brain 
  } from 'lucide-react';

  interface ServicesProps {
    mvpLink: string;
    smeLink: string;
    aiLink: string;
    webDevLink: string;
    mobileDevLink: string;
    uiLink: string;
    analyticsLink: string;
  }

const Services: React.FC<ServicesProps> = ({mvpLink, smeLink, aiLink, webDevLink, mobileDevLink, uiLink, analyticsLink}) => {

    return (
        <>
        <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4 text-inter">
              Our Expertise
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900 text-inter">
              Tailored Technology Solutions
            </h2>
            <p className="md:text-lg text-gray-600 text-inter">
              We provide a comprehensive range of technology services customized to meet your unique business requirements and challenges.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-16">
            {/* MVP Development */}
            <div id="web" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="w-full p-0 md:w-1/2">
                    <div className="rounded-xl overflow-hidden shadow-xl  scale-[0.9]">
                      <div className="aspect-video bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                        <Keyboard size={80} className="text-white opacity-50" />
                      </div>
                    </div>
                </div>
                
                <div className="w-full md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600 mr-4">
                      <Keyboard size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Rapid MVP Development for Startups</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                  You're building something bold. You need a tech partner who gets it. We specialize in turning innovative ideas into working products—fast.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">MVP Strategy & Roadmapping</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">UX/UI Design</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Full-Stack Development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Quick Prototyping & Iteration</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Deployment & User Feedback</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Investor-Ready Outputs</p>
                    </div>
                    <Link to={mvpLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>


          {/* SME Digital  Transformation*/}
          <div id="web" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row md:flex-row-reverse items-center gap-8 md:gap-12">
              <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl  scale-[0.9]">
                    <div className="aspect-video bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                      <QrCode size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600 mr-4">
                      <QrCode size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900 text-inter">End-to-End Digital Transformation for SMEs</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                  We empower small and medium businesses with the tools, strategies, and technologies needed to thrive in the digital age.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Business Process Automation</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">ERP/CRM Implementation</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Website & App Development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Cloud & Infrastructure Migration</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Data Analytics & Dashboards</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">E-commerce Enablement</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Custom Software Solutions</p>
                    </div>
                    <Link to={smeLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Services */}
            <div id="ai" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl  scale-[0.9]">
                    <div className="aspect-video bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center">
                      <Brain  size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 mr-4">
                      <Brain  size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900  text-inter">AI Services</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                  We offer a wide range of AI services tailored to meet your specific business needs and challenges.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700  text-inter">Machine Learning</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Natural Language Processing</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">AI-Powered Data Analytics</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">ChatBot and LLM services</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">AI Agents</p>
                    </div>
                    <Link to={aiLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Web Development */}
            <div id="web" className="scroll-mt-24">
            <div className="flex flex-col flex-col-reverse md:flex-row items-center gap-8 md:gap-12">
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-green-100 text-green-600 mr-4">
                      <Code size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Web Development</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    We create custom web applications and responsive websites that deliver exceptional user experiences. Our team of experienced developers uses the latest technologies and best practices to build scalable and high-performance web solutions.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Custom web application development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Responsive website design and development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">E-commerce solutions</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Progressive Web Applications (PWAs)</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">API development and integration</p>
                    </div>
                    <Link to={webDevLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="md:w-1/2 w-full">
                  <div className="rounded-xl overflow-hidden shadow-xl scale-[0.9]">
                  <div className="aspect-video bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center">
                      <Code size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile App Development */}
            <div id="mobile" className="scroll-mt-24">
              <div className="flex flex-col  flex-col-reverse md:flex-row-reverse items-center gap-8 md:gap-12">
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-purple-100 text-purple-600 mr-4">
                      <Smartphone size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Mobile App Development</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    We build native and cross-platform mobile applications for iOS and Android that engage users and drive business growth. Our mobile development team focuses on creating intuitive interfaces and smooth user experiences.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Native iOS and Android app development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Cross-platform app development (React Native, Flutter)</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">App redesign and modernization</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Mobile app testing and quality assurance</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">App store optimization and deployment</p>
                    </div>
                    <Link to={mobileDevLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl  scale-[0.9]">
                    <div className="aspect-video bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <Smartphone size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* UI/UX Design */}
            <div id="design" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
                <div className="md:w-1/2 w-full">
                  <div className="rounded-xl overflow-hidden shadow-xl scale-[0.9]">
                    <div className="aspect-video bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                      <PenTool size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-orange-100 text-orange-600 mr-4">
                      <PenTool size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">UI/UX Design</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    We take a user-centered design approach to create intuitive interfaces that delight users and improve conversion rates. Our design team combines creativity with data-driven insights to deliver exceptional digital experiences.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-orange-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">User research and persona development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-orange-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Wireframing and prototyping</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-orange-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Visual design and branding</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-orange-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Usability testing and optimization</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-orange-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Responsive and adaptive design</p>
                    </div>
                    <Link to={uiLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Data Analytics */}
            <div id="analytics" className="scroll-mt-24">
              <div className="flex flex-col flex-col-reverse md:flex-row-reverse items-center gap-8 md:gap-12">
                <div className="md:w-1/2 px-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-green-100 text-green-600 mr-4">
                      <LineChart size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Data Analytics</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    We help you transform raw data into actionable insights with custom dashboards and business intelligence solutions. Our data specialists work with you to uncover valuable patterns and trends that drive informed decision-making.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-green-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Data warehouse design and implementation</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-green-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Business intelligence dashboard development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-green-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">ETL process design and optimization</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-green-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Predictive analytics and forecasting</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-green-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Data visualization and reporting</p>
                    </div>
                    <Link to={analyticsLink} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-indigo-600">
                        Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl scale-[0.9]">
                    <div className="aspect-video bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center">
                      <LineChart size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </>
      )
    }

export default Services;